import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/themes/shared/ThemeContext";
import StorefrontLayout from "@/components/StorefrontLayout";
import Home from "@/pages/Home";
import VisitorTracker from "@/components/VisitorTracker";

const OrderForm = lazy(() => import("@/pages/OrderForm"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const CheckoutCancel = lazy(() => import("@/pages/CheckoutCancel"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Account = lazy(() => import("@/pages/Account"));
const Track = lazy(() => import("@/pages/Track"));
const Contact = lazy(() => import("@/pages/Contact"));
const Packages = lazy(() => import("@/pages/Packages"));
const Reviews = lazy(() => import("@/pages/Reviews"));
const WriteReview = lazy(() => import("@/pages/WriteReview"));
const Download = lazy(() => import("@/pages/Download"));
const LegalCenter = lazy(() => import("@/pages/LegalCenter"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// ⚡ LAZY LOADING pentru Admin (încarcă doar când e nevoie)
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminOrders = lazy(() => import("@/pages/admin/Orders"));
const Customers = lazy(() => import("@/pages/admin/Customers"));
const Products = lazy(() => import("@/pages/admin/Products"));
const AdminReviews = lazy(() => import("@/pages/admin/Reviews"));
const PromoCodes = lazy(() => import("@/pages/admin/PromoCodes"));
const LegalManager = lazy(() => import("@/pages/admin/LegalManager"));
const FAQManager = lazy(() => import("@/pages/admin/FAQManager"));
const AddonsManager = lazy(() => import("@/pages/admin/AddonsManager"));
const Activity = lazy(() => import("@/pages/admin/Activity"));
const Reports = lazy(() => import("@/pages/admin/Reports"));
const Settings = lazy(() => import("@/pages/admin/Settings"));
const MarketingConsents = lazy(() => import("@/pages/admin/MarketingConsents"));
const MarketingEmails = lazy(() => import("@/pages/admin/MarketingEmails"));
const Inbox = lazy(() => import("@/pages/admin/Inbox"));
const AdminNotifications = lazy(() => import("@/pages/admin/Notifications"));
const Health = lazy(() => import("@/pages/admin/Health"));
const SeoAudit = lazy(() => import("@/pages/admin/SeoAudit"));

// Loading spinner
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium">Se încarcă...</p>
    </div>
  </div>
);

const RequireAuth = ({ children, role }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
};

const ScrollToTop = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, search]);

  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <ScrollToTop />
            <VisitorTracker />
            <Routes>
              <Route element={<StorefrontLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/comanda" element={<Suspense fallback={<LoadingFallback />}><OrderForm /></Suspense>} />
                <Route path="/pachete" element={<Suspense fallback={<LoadingFallback />}><Packages /></Suspense>} />
                <Route path="/recenzii" element={<Suspense fallback={<LoadingFallback />}><Reviews /></Suspense>} />
                <Route path="/recenzii/scrie" element={<Suspense fallback={<LoadingFallback />}><WriteReview /></Suspense>} />
                <Route path="/faq" element={<Suspense fallback={<LoadingFallback />}><FAQ /></Suspense>} />
                <Route path="/track" element={<Suspense fallback={<LoadingFallback />}><Track /></Suspense>} />
                <Route path="/contact" element={<Suspense fallback={<LoadingFallback />}><Contact /></Suspense>} />
                <Route path="/login" element={<Suspense fallback={<LoadingFallback />}><Login /></Suspense>} />
                <Route path="/register" element={<Suspense fallback={<LoadingFallback />}><Register /></Suspense>} />
                <Route path="/cont" element={<RequireAuth><Suspense fallback={<LoadingFallback />}><Account /></Suspense></RequireAuth>} />
                <Route path="/checkout/success" element={<Suspense fallback={<LoadingFallback />}><CheckoutSuccess /></Suspense>} />
                <Route path="/checkout/cancel" element={<Suspense fallback={<LoadingFallback />}><CheckoutCancel /></Suspense>} />
                <Route path="*" element={<Suspense fallback={<LoadingFallback />}><NotFound /></Suspense>} />
              </Route>

              {/* Public download page - no layout */}
              <Route path="/download/:id" element={<Suspense fallback={<LoadingFallback />}><Download /></Suspense>} />
              
              {/* Legal Center */}
              <Route path="/legal/:page" element={<Suspense fallback={<LoadingFallback />}><LegalCenter /></Suspense>} />
              <Route path="/legal" element={<Navigate to="/legal/termeni" replace />} />

              <Route path="/admin" element={
                <RequireAuth role="admin">
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminLayout />
                  </Suspense>
                </RequireAuth>
              }>
                <Route index element={<Suspense fallback={<LoadingFallback />}><Dashboard /></Suspense>} />
                <Route path="orders" element={<Suspense fallback={<LoadingFallback />}><AdminOrders /></Suspense>} />
                <Route path="customers" element={<Suspense fallback={<LoadingFallback />}><Customers /></Suspense>} />
                <Route path="products" element={<Suspense fallback={<LoadingFallback />}><Products /></Suspense>} />
                <Route path="addons" element={<Suspense fallback={<LoadingFallback />}><AddonsManager /></Suspense>} />
                <Route path="reviews" element={<Suspense fallback={<LoadingFallback />}><AdminReviews /></Suspense>} />
                <Route path="promo" element={<Suspense fallback={<LoadingFallback />}><PromoCodes /></Suspense>} />
                <Route path="legal" element={<Suspense fallback={<LoadingFallback />}><LegalManager /></Suspense>} />
                <Route path="faq" element={<Suspense fallback={<LoadingFallback />}><FAQManager /></Suspense>} />
                <Route path="reports" element={<Suspense fallback={<LoadingFallback />}><Reports /></Suspense>} />
                <Route path="activity" element={<Suspense fallback={<LoadingFallback />}><Activity /></Suspense>} />
                <Route path="settings" element={<Suspense fallback={<LoadingFallback />}><Settings /></Suspense>} />
                <Route path="marketing" element={<Suspense fallback={<LoadingFallback />}><MarketingConsents /></Suspense>} />
                <Route path="marketing-emails" element={<Suspense fallback={<LoadingFallback />}><MarketingEmails /></Suspense>} />
                <Route path="inbox" element={<Suspense fallback={<LoadingFallback />}><Inbox /></Suspense>} />
                <Route path="notifications" element={<Suspense fallback={<LoadingFallback />}><AdminNotifications /></Suspense>} />
                <Route path="health" element={<Suspense fallback={<LoadingFallback />}><Health /></Suspense>} />
                <Route path="seo-audit" element={<Suspense fallback={<LoadingFallback />}><SeoAudit /></Suspense>} />
                <Route path="*" element={<Suspense fallback={<LoadingFallback />}><NotFound /></Suspense>} />
              </Route>
              <Route path="*" element={<Suspense fallback={<LoadingFallback />}><NotFound /></Suspense>} />
            </Routes>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;

